import { useMemo } from 'react'

import {
  GetProjectTimeRecordsDocument,
  ProjectsAssignedToProjectWorkersDocument,
} from 'generated/resources/graphql'

import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'

import { ProjectTimeRecords } from './projectTimeRecord/useGetProjectTimeRecords'
import { ProjectWorkers } from './projectWorker/useGetProjectsAssignedToWorker'

export interface IUseGetEmployeeProjectsTrackingMonth {
  employeeId: string
  startDate: Date
  endDate: Date
  includeSubprojects: boolean
  skip?: boolean
}

export interface IUseGetEmployeeProjectsTrackingMonthData {
  projectsAssigned: ProjectWorkers
  timeRecords: ProjectTimeRecords
  hasProjectsToTrack: boolean
  refetchEmployeeTimeRecords: () => Promise<void>
  fetchingTimeRecords: boolean
  fetchingProjectWorkers: boolean
}

export interface IUseGetEmployeeProjectsTrackingMonthReturn {
  queryData: IUseGetEmployeeProjectsTrackingMonthData
  getProjectNameForShift: (attendanceShiftId: string) => string | null | undefined
  getSubprojectNameForShift: (attendanceShiftId: string) => string | null | undefined
}

export const useGetEmployeeProjectsTrackingByMonth = ({
  employeeId,
  startDate,
  endDate,
  includeSubprojects,
  skip = false,
}: IUseGetEmployeeProjectsTrackingMonth): IUseGetEmployeeProjectsTrackingMonthReturn => {
  const variables = {
    employeeIds: [employeeId],
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
    includeSubprojects,
  }

  const { data: projectsData, loading: fetchingProjectWorkers } = useQuery(
    ProjectsAssignedToProjectWorkersDocument,
    {
      variables: {
        employeeIds: [employeeId],
        onlyActiveProjects: true,
        assigned: true,
        includeSubprojects,
      },
      skip: skip || !employeeId,
      fetchPolicy: 'cache-first',
    }
  )

  const {
    data: timeRecordsData,
    loading: fetchingTimeRecords,
    refetch,
  } = useQuery(GetProjectTimeRecordsDocument, {
    variables,
    skip: skip || !employeeId,
  })

  const timeRecords = timeRecordsData?.projectManagement.timeRecords || []
  const projectsAssigned = useMemo(() => {
    return projectsData?.projectManagement.projectWorkers || []
  }, [projectsData])

  const hasProjectsToTrack = useUnsafeMemo(() => projectsAssigned.length > 0, [projectsAssigned])

  const getProjectNameForShift = (attendanceShiftId: string) => {
    const timeRecord = timeRecords.find((tr) => tr.attendanceShiftId === attendanceShiftId)
    return timeRecord?.projectWorker?.imputableProject?.name || null
  }

  const getSubprojectNameForShift = (attendanceShiftId: string) => {
    const timeRecord = timeRecords.find((tr) => tr.attendanceShiftId === attendanceShiftId)
    return timeRecord?.subproject?.name || null
  }

  const refetchEmployeeTimeRecords = async () => {
    await refetch()
  }

  return {
    queryData: {
      projectsAssigned,
      timeRecords,
      hasProjectsToTrack,
      refetchEmployeeTimeRecords,
      fetchingTimeRecords,
      fetchingProjectWorkers,
    },
    getProjectNameForShift,
    getSubprojectNameForShift,
  }
}
